import React from 'react'
import { CircularProgress } from '@mui/material'
import styled from 'styled-components'

import { ToolTip } from 'components/atoms/TollTip'
import { ButtonVariantProps } from 'types/styles'
import { noop } from 'utils/shared'
import { theme } from 'utils/theme'

interface ButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode
  variant?: 'primary' | 'secondary' | 'dark' | 'outlined'
  size?: 'fit' | 'small' | 'medium' | 'large'
  disabled?: boolean
  starticon?: React.ReactElement
  endicon?: React.ReactElement
  type?: 'submit' | 'button'
  loading?: boolean
  tooltipMessage?: string
  fontSize?: string
}

export const Button = (props: ButtonProps) => {
  const {
    onClick = noop,
    children,
    variant = 'primary',
    size = 'medium',
    disabled = false,
    starticon,
    endicon,
    loading,
    tooltipMessage,
    fontSize,
    ...restProps
  } = props

  const getVariantColors = (): ButtonVariantProps => {
    switch (variant) {
      case 'primary':
        return theme.colors.button.primary
      case 'secondary':
        return theme.colors.button.secondary
      case 'outlined':
        return theme.colors.button.outlined
      default:
        return theme.colors.button.primary
    }
  }

  const Button = (
    <StyledButton
      {...restProps}
      data-size={size}
      disabled={loading || disabled}
      $colors={getVariantColors()}
      onClick={event => {
        if (loading || disabled) return
        onClick(event)
      }}
      tabIndex={0}
      $fontSize={fontSize}
    >
      {starticon && !loading ? (
        <StartIconContainer>{starticon}</StartIconContainer>
      ) : null}
      {loading ? (
        <StartIconContainer>
          <CircularProgress style={{ color: theme.colors.primary }} size={20} />
        </StartIconContainer>
      ) : null}
      <span>{children}</span>
      {endicon && <EndIconContainer>{endicon}</EndIconContainer>}
    </StyledButton>
  )

  return (
    <>
      {disabled && tooltipMessage ? (
        <ToolTip titleContent={tooltipMessage} placement='bottom'>
          <span>{Button}</span>
        </ToolTip>
      ) : (
        Button
      )}
    </>
  )
}

const StyledButton = styled.button<{
  $colors: ButtonVariantProps
  $fontSize?: string
}>`
  width: fit-content;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: 10px;
  outline: none;
  box-sizing: border-box;
  padding: 8px;
  line-height: 16px;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ $fontSize }) => $fontSize || '14px'};
  font-weight: 700;
  letter-spacing: 0;

  &[data-size='fit'] {
    padding: 8px 22px;
    width: fit-content;
  }

  &[data-size='small'] {
    padding: 8px 22px;
    min-width: 208px;
    width: fit-content;
  }

  &[data-size='medium'] {
    padding: 8px 22px;
    min-width: 208px;
    width: fit-content;
  }

  &[data-size='large'] {
    padding: 8px 22px;
    width: 100%;
  }

  color: ${({ $colors }) => $colors.font};
  background: ${({ $colors }) => $colors.background};
  border: ${({ $colors }) => $colors.border};
  box-shadow: ${({ $colors }) => $colors.boxShadow};

  &:disabled {
    cursor: not-allowed;
    color: ${({ $colors }) => $colors.disabled.font};
    background: ${({ $colors }) => $colors.disabled.background};
  }

  &:hover:enabled {
    color: ${({ $colors }) => $colors.fontHover || $colors.font};
    background: ${({ $colors }) => $colors.hover || $colors.background};
    border-color: transparent;
  }

  &:active:enabled {
    color: ${({ $colors }) => $colors.fontAction || $colors.font};
    background: ${({ $colors }) => $colors.action || $colors.background};
  }
`

const StartIconContainer = styled.div`
  margin-right: 4px;
`

const EndIconContainer = styled.div`
  margin-left: 4px;
`
